import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
// Material imports
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
//Limitless imports
import { withi18n } from "../../../customer/components";
import RowsTemplate from "../content/templates/RowsTemplate";
import PortalButton from "../buttons/portal-button/PortalButton";
import Checkbox from "../form-elements/check-box/Checkbox";
import { useAppConfig } from "@limitless/common/hooks";
import { ThemeProvider } from "@emotion/react";
import useCustomTheme from "expert-web-app/src/utils/hooks/useCustomTheme";
import { useExpertProfileContext } from "@limitless/expert";
import { TermsDialog } from "@limitless/portal";
const templateMapping = {
  basic: 1,
  twoRows: 2
};
const AnnouncementContent = ({
  announcement,
  onDismiss,
  classes,
  t,
  onSave,
  remoteContentComponent: RemoteContent,
  isCrowdManager = false,
  title,
  date
}) => {
  const {
    template,
    acknowledge
  } = announcement;
  const {
    getContactUsUrl
  } = useAppConfig();
  const history = useHistory();
  const {
    profile
  } = useExpertProfileContext();
  const theme = useCustomTheme();
  const [termsDialogOpen, setTermsDialogOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const getAcknowledged = React.useCallback(() => {
    var _announcement$templat;
    if (((_announcement$templat = announcement.template) === null || _announcement$templat === void 0 ? void 0 : _announcement$templat.type) === "acceptTermsAndConditions") {
      return !!announcement.acknowledged;
    }
    return !(announcement.acknowledge && !announcement.acknowledged);
  }, [announcement]);
  const [acknowledged, setAcknowledged] = React.useState(getAcknowledged());
  React.useEffect(() => {
    setAcknowledged(getAcknowledged());
  }, [announcement.announcementId, getAcknowledged]);
  const handleDismiss = () => {
    setLoading(true);
    onDismiss(announcement, () => setLoading(false));
  };
  const handleContactUsClick = () => {
    if (onSave) onSave();
    if (!isCrowdManager) {
      const navUrl = "/contactusscreen";
      const url = getContactUsUrl(navUrl);
      url === navUrl ? history.push({
        pathname: url,
        state: announcement
      }) : window.open(url, "_blank");
    }
  };
  const handleAcknowledgeToggle = () => {
    if ((template === null || template === void 0 ? void 0 : template.type) === "acceptTermsAndConditions") {
      if (acknowledged) {
        setAcknowledged(false);
      } else {
        setTermsDialogOpen(true);
      }
    } else {
      setAcknowledged(prev => !prev);
    }
  };
  const renderButtons = () => {
    const isTemplateTermChange = (template === null || template === void 0 ? void 0 : template.type) === "acceptTermsAndConditions";
    const showDelete = typeof onSave === "undefined" && (template === null || template === void 0 ? void 0 : template.addContactUsAction) || !(template !== null && template !== void 0 && template.addContactUsAction);
    return /*#__PURE__*/React.createElement("div", {
      className: classes.dismissContainer
    }, (template === null || template === void 0 ? void 0 : template.addContactUsAction) && /*#__PURE__*/React.createElement(PortalButton, {
      variant: "outlined",
      className: classes.buttonPadding,
      onClick: handleContactUsClick,
      id: "contactUsAnnouncementButton",
      disabled: !acknowledged,
      style: {
        borderColor: theme.palette.primary.main
      }
    }, t("Contact Us")), showDelete && !(isTemplateTermChange && onSave) && /*#__PURE__*/React.createElement(PortalButton, {
      variant: "outlined",
      className: classes.buttonPadding,
      onClick: handleDismiss,
      disabled: !acknowledged,
      id: "dismissAnnouncementButton",
      loading: loading,
      style: {
        borderColor: theme.palette.primary.main
      }
    }, t("Delete")), onSave && /*#__PURE__*/React.createElement(PortalButton, {
      variant: "contained",
      onClick: onSave,
      disabled: !acknowledged,
      id: "readLaterButton"
    }, t("Save")));
  };
  const Template = template ? RowsTemplate : null;
  const numOfRows = templateMapping[template === null || template === void 0 ? void 0 : template.name] || 1;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.announcementContentContainer,
    style: {
      backgroundColor: theme.palette.background.elevation1
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.headerContainer
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    color: "textPrimary"
  }, title), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "textSecondary"
  }, date)), /*#__PURE__*/React.createElement("div", {
    className: classes.contentContainer,
    id: "announcementContent",
    style: {
      fontFamily: theme.typography.body2.fontFamily,
      fontSize: theme.typography.body2.fontSize
    }
  }, Template ? /*#__PURE__*/React.createElement(Template, {
    components: template.component,
    numberOfRows: numOfRows,
    renderRow: (index, component) => /*#__PURE__*/React.createElement(RemoteContent, {
      content: component.value,
      wrapWhiteSpace: false,
      allowNewWindow: true
    })
  }) : /*#__PURE__*/React.createElement(Typography, null, t("Announcement template not supported")), (acknowledge || (template === null || template === void 0 ? void 0 : template.type) === "acceptTermsAndConditions") && /*#__PURE__*/React.createElement("div", {
    className: classes.requiresAckContainer
  }, /*#__PURE__*/React.createElement(Checkbox, {
    name: "acknowledged",
    value: "acknowledged",
    checked: acknowledged,
    label: (template === null || template === void 0 ? void 0 : template.type) === "acceptTermsAndConditions" ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "primary",
      style: {
        cursor: "pointer",
        textDecoration: "underline"
      },
      onClick: e => {
        e.preventDefault();
        setTermsDialogOpen(true);
      }
    }, t("I have read and agree to the Terms of Service and Code of Conduct")) : /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "textPrimary"
    }, t("I acknowledge that I have read this announcement and agree to its terms")),
    id: "acknowledgedCheckBox",
    disabled: announcement.acknowledge && announcement.acknowledged || (template === null || template === void 0 ? void 0 : template.type) === "acceptTermsAndConditions" && announcement.acknowledged,
    onChange: handleAcknowledgeToggle,
    capitalize: false,
    checkboxLabelRoot: classes.checkboxLabel,
    style: {
      paddingLeft: "0px"
    }
  }), renderButtons()), !acknowledge && (template === null || template === void 0 ? void 0 : template.type) !== "acceptTermsAndConditions" && renderButtons())), /*#__PURE__*/React.createElement(TermsDialog, {
    open: termsDialogOpen,
    showOnlyPrivacy: false,
    onClose: () => setTermsDialogOpen(false),
    className: classes.termsDialogOverrides,
    locale: (profile === null || profile === void 0 ? void 0 : profile.userLocale) || "en-GB",
    readOnly: !!announcement.acknowledged,
    acceptTerms: () => {
      setAcknowledged(true);
      setTermsDialogOpen(false);
    }
  }));
};
const styles = theme => ({
  checkboxLabel: {
    width: "100%"
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0px 24px"
  },
  announcementContentContainer: {
    margin: 60,
    display: "flex",
    flexDirection: "column",
    padding: 20,
    borderRadius: 24,
    maxHeight: "80vh",
    overflowY: "auto"
  },
  contentContainer: {
    backgroundColor: "transparent",
    padding: "0px 24px"
  },
  requiresAckContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "left"
  },
  dismissContainer: {
    display: "flex",
    justifyContent: "flex-start",
    margin: "20px 0px",
    position: "relative",
    width: "100%"
  },
  buttonPadding: {
    marginRight: 16
  }
});
AnnouncementContent.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  announcement: PropTypes.object.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  remoteContentComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.object]).isRequired,
  isCrowdManager: PropTypes.bool,
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired
};
export default withStyles(styles)(withi18n(AnnouncementContent));